import { createStyles, makeStyles } from '@material-ui/styles';
import * as classNames from 'classnames';
import isTouchDevice from 'is-touch-device';
import * as React from 'react';
import { FC, useEffect, useRef, useState } from 'react';
import Flickity from 'react-flickity-component';
import mediaQueries from 'src/constants/responsive';
import spacing from 'src/constants/spacing';
import stylesheet, { secondaryColor } from 'src/constants/stylesheet';
import { getAlt, getLegend } from 'src/utils/legendAlt';
import ViewMonitor from 'src/utils/ViewMonitor';
import previousCur from '../../Assets/slider-left-white.cur';
import previousPng from '../../Assets/slider-left-white.svg';
import nextCur from '../../Assets/slider-right-white.cur';
import nextPng from '../../Assets/slider-right-white.svg';
import Image from '../Image';

const useStyles = makeStyles(() =>
  createStyles({
    imagesContainer: {
      width: '100%',
      [mediaQueries.Mobile]: {
        paddingLeft: spacing.GutterGrid.Mobile / 2,
        paddingRight: spacing.GutterGrid.Mobile / 2,

        paddingBottom: spacing.Gutter.Mobile / 2,
      },
      [mediaQueries.Tablet]: {
        paddingLeft: spacing.GutterGrid.Tablet / 2,
        paddingRight: spacing.GutterGrid.Tablet / 2,

        paddingBottom: spacing.Gutter.Tablet / 2,
      },
      [mediaQueries.Laptop]: {
        paddingLeft: spacing.GutterGrid.Laptop / 2,
        paddingRight: spacing.GutterGrid.Laptop / 2,

        paddingBottom: spacing.Gutter.Laptop / 2,
      },
      [mediaQueries.Desktop]: {
        paddingLeft: spacing.GutterGrid.Desktop / 2,
        paddingRight: spacing.GutterGrid.Desktop / 2,

        paddingBottom: spacing.Gutter.Desktop / 2,
      },
      [mediaQueries.BigDesktop]: {
        paddingLeft: spacing.GutterGrid.BigDesktop / 2,
        paddingRight: spacing.GutterGrid.BigDesktop / 2,

        paddingBottom: spacing.Gutter.BigDesktop / 2,
      }
    },
    carouselContainer: {
      position: 'relative',
      height: 0,
      overflow: 'hidden',
    },
    '@global': {
      '.panel-carousel': {
        position: 'absolute',
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        overflow: 'hidden',
        outline: 'none'
      },
      '.panel-carousel .flickity-viewport': {
        height: '100%'
      },
      '.panel-carousel .flickity-slider': {
        height: '100%'
      },
      '.panel-carousel .flickity-button': {
        background: 'transparent',
      },
      /* big previous & next buttons */
      '.panel-carousel .flickity-prev-next-button.previous': {
        position: 'absolute',
        width: '50%',
        top: 0,
        left: 0,
        height: '100%',
        zIndex: 1,
        padding: 0,
        border: 0,
        '&': {
          cursor: `url(${previousPng}),w-resize`,
        },
        cursor: `url(${previousCur}) 20 20 ,w-resize`,
      },
      '.panel-carousel .flickity-prev-next-button.next': {
        position: 'absolute',
        width: '50%',
        top: 0,
        right: 0,
        height: '100%',
        zIndex: 1,
        padding: 0,
        border: 0,
        '&': {
          cursor: `url(${nextPng}),e-resize`,
        },
        cursor: `url(${nextCur}) 20 20 ,e-resize`,
      },
      /* icon color */
      '.panel-carousel .flickity-button-icon': {
        display: 'none'
      },
    },
    imageContainer: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%'
    },
    image: {
      width: '100%',
      height: '100%',
      objectFit: 'contain',
      objectPosition: 'center left',
      ...stylesheet.ObjectFitContain
    },
    legend: {
      position: 'relative',
      ...stylesheet.Caption,
      color: secondaryColor,
      display: 'none',
      [mediaQueries.Mobile]: {
        marginTop: spacing.GutterGrid.Mobile / 3,
      },
      [mediaQueries.Tablet]: {
        marginTop: spacing.GutterGrid.Tablet / 3,
      },
      [mediaQueries.Laptop]: {
        marginTop: spacing.GutterGrid.Laptop / 3,
      },
      [mediaQueries.Desktop]: {
        marginTop: spacing.GutterGrid.Desktop / 3,
      },
      [mediaQueries.BigDesktop]: {
        marginTop: spacing.GutterGrid.BigDesktop / 3,
      }
    },
    showLegend: {
      display: 'block',
    },
    LegendPadding: {
      paddingLeft: 50,
    },
    legendCounter: {
      position: 'absolute',
      left: 0,
      top: 0,
    },
    legendText: {
      display: 'block'
    },
    hide: {
      ...stylesheet.Hide,
      transitionDelay: '100ms'
    },
    show: {
      ...stylesheet.Show
    },
  }));

export type PanelImages = PanelImagesDto;

type Props = PanelImages;

const PanelImages: FC<Props> = ({ Images, children }) => {
  const cls = useStyles();
  const [height, sertHeight] = useState(0);

  const flickity = useRef<any>(null);

  React.useEffect(() => {
    // Magical number :/
    // issue #22
    let h = 300;
    Images.map(image => {
      if (image.Width <= 0) { return; }
      const ratio = image.Height / image.Width * 100;

      if (ratio >= h) { return; }

      h = ratio;
    });

    sertHeight(h);
  }, [])

  const [currentTouched, setCurrentTouched] = useState(0);

  useEffect(
    () => {
      if (flickity.current) {
        flickity.current.on('change', (index: number) => {
          setCurrentTouched(index);
        });
      }
    },
    [flickity, setCurrentTouched]
  )

  const legends = Images.map((s, i) =>
    <div
      key={i}
      className={
        classNames(
          cls.legend,
          currentTouched === i && cls.showLegend,
          Images.length > 1 && cls.LegendPadding)}
    >
      {Images.length > 1 &&
        <div className={cls.legendCounter}>{`${i + 1}/${Images.length}`}</div>
      }
      <div
        className={cls.legendText}
        dangerouslySetInnerHTML={{ __html: getLegend(s) }}
      />
    </div>
  );

  const notTouch = !isTouchDevice();

  return (
    <ViewMonitor once={true}>{(intersect) =>
      <div className={classNames(cls.imagesContainer, cls.hide, intersect && cls.show)}>
        <div
          className={cls.carouselContainer}
          style={{
            paddingBottom: `${height}%`,
            overflow: 'hidden'
          }}
        >
          <Flickity
            className={'panel-carousel'} // default ''
            elementType={'div'} // default 'div'
            options={{
              pageDots: false,
              prevNextButtons: notTouch && Images.length > 1,
              wrapAround: true,
              setGallerySize: false,
              autoPlay: 6000
            }} // takes flickity options {}
            disableImagesLoaded={false} // default false
            // tslint:disable-next-line:jsx-no-lambda
            flickityRef={(r) => flickity.current = r}
          >
            {Images.map((s, i) =>
              <div
                key={i}
                className={cls.imageContainer}>
                <Image
                  className={cls.image}
                  pictureRef={s}
                  sizes="100vw"
                  alt={getAlt(s)}
                />
              </div>
            )}
          </Flickity>
        </div>
        {legends}
        {children}
      </div>}
    </ViewMonitor>
  );
};

export default PanelImages;


// WEBPACK FOOTER //
// ./src/Components/Panels/PanelImages.tsx